import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  alpha,
  useTheme,
  LinearProgress,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  Paper,
  Zoom,
} from '@mui/material';
import {
  AttachMoney,
  PeopleAlt,
  VolunteerActivism,
  ArrowUpward,
  ArrowDownward,
  TrendingUp,
  MoreVert,
  Info,
  ArrowForward,
  InfoOutlined,
  CalendarToday,
  Diversity3,
  MonetizationOn,
  ShowChart,
  CreditCard,
  Handshake,
  ReceiptLong,
  Person,
  Groups,
  Event,
  Loyalty,
  PriceCheck,
  AssignmentInd,
} from '@mui/icons-material';
import { Metrics } from '../../../../types/metrics';

interface MetricsCardsProps {
  metrics: Metrics | null;
  donationGrowth: number;
  donorGrowth: number;
  volunteerGrowth: number;
  onOpenDonationsModal: () => void;
  onOpenDonorsModal: () => void;
  onOpenVolunteersModal: () => void;
}

// Helper function to format currency
const formatCurrency = (amount: number | undefined) => {
  if (amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format numbers with commas
const formatNumber = (num: number | undefined) => {
  if (num === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

const MetricsCards: React.FC<MetricsCardsProps> = ({
  metrics,
  donationGrowth,
  donorGrowth,
  volunteerGrowth,
  onOpenDonationsModal,
  onOpenDonorsModal,
  onOpenVolunteersModal,
}) => {
  const theme = useTheme();

  // Data for tooltips and additional context
  const tooltipData = {
    donations: "Total amount of all donations received to date. The percentage shows growth compared to the previous period.",
    donors: "Total number of unique donors who have contributed. The percentage shows donor growth compared to the previous period.",
    volunteers: "Total number of volunteers who have signed up. The percentage shows volunteer growth compared to the previous period."
  };

  // Set targets (this could be fetched from your backend in the future)
  const targetDonations = (metrics?.totalDonations || 0) * 1.2; // 20% higher than current
  const targetDonors = (metrics?.totalDonors || 0) * 1.15; // 15% higher than current
  const targetVolunteers = (metrics?.totalVolunteers || 0) * 1.1; // 10% higher than current

  // Calculate progress percentages
  const donationsProgress = ((metrics?.totalDonations || 0) / targetDonations) * 100;
  const donorsProgress = ((metrics?.totalDonors || 0) / targetDonors) * 100;
  const volunteersProgress = ((metrics?.totalVolunteers || 0) / targetVolunteers) * 100;

  // Average donation calculation
  const avgDonation = metrics?.totalDonors && metrics?.totalDonors > 0
    ? (metrics?.totalDonations || 0) / metrics.totalDonors
    : 0;

  return (
    <Zoom in={true} style={{ transitionDelay: '100ms' }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Donations */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              borderRadius: 2,
              boxShadow: theme.shadows[3],
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
                '& .metric-action-button': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
                '& .metric-icon': {
                  transform: 'scale(1.1)',
                }
              }
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                height: '6px', 
                background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)` 
              }} 
            />
            
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    className="metric-icon"
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.15), 
                      color: theme.palette.info.main,
                      p: 1.5,
                      width: 56,
                      height: 56,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <MonetizationOn fontSize="large" />
                  </Avatar>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: -0.5 }}>
                      TOTAL DONATIONS
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        letterSpacing: '-0.5px',
                        color: theme.palette.info.dark
                      }}
                      aria-live="polite"
                    >
                      {formatCurrency(metrics?.totalDonations)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Tooltip title={tooltipData.donations}>
                    <IconButton size="small" sx={{ mb: 0.5 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Chip 
                    icon={donationGrowth >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    label={`${Math.abs(donationGrowth).toFixed(1)}%`}
                    color={donationGrowth >= 0 ? "success" : "error"}
                    size="small"
                    variant="filled"
                    sx={{ 
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      boxShadow: theme.shadows[1]
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mt: 3, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress to goal
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
                    {donationsProgress.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(donationsProgress, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 5,
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`
                    }
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 2, opacity: 0.6 }} />
              
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Avg. Donation
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(avgDonation)}
                    </Typography>
                    <CreditCard fontSize="inherit" color="disabled" sx={{ mt: '2px' }} />
                  </Stack>
                </Box>
                
                <Button 
                  className="metric-action-button"
                  variant="outlined"
                  size="small" 
                  color="info"
                  endIcon={<ArrowForward />}
                  onClick={onOpenDonationsModal}
                  sx={{ 
                    mt: 0, 
                    borderRadius: '12px',
                    opacity: { xs: 1, md: 0.8 },
                    transform: { xs: 'none', md: 'translateX(8px)' },
                    transition: 'opacity 0.3s, transform 0.3s',
                  }}
                >
                  View All
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Donors */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              borderRadius: 2,
              boxShadow: theme.shadows[3],
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
                '& .metric-action-button': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
                '& .metric-icon': {
                  transform: 'scale(1.1)',
                }
              }
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                height: '6px', 
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)` 
              }} 
            />
            
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    className="metric-icon"
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.15), 
                      color: theme.palette.primary.main,
                      p: 1.5,
                      width: 56,
                      height: 56,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <PeopleAlt fontSize="large" />
                  </Avatar>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: -0.5 }}>
                      TOTAL DONORS
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        letterSpacing: '-0.5px',
                        color: theme.palette.primary.dark
                      }}
                      aria-live="polite"
                    >
                      {formatNumber(metrics?.totalDonors)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Tooltip title={tooltipData.donors}>
                    <IconButton size="small" sx={{ mb: 0.5 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Chip 
                    icon={donorGrowth >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    label={`${Math.abs(donorGrowth).toFixed(1)}%`}
                    color={donorGrowth >= 0 ? "success" : "error"}
                    size="small"
                    variant="filled"
                    sx={{ 
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      boxShadow: theme.shadows[1]
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mt: 3, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress to goal
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {donorsProgress.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(donorsProgress, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 5,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
                    }
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 2, opacity: 0.6 }} />
              
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Recurring Donors
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatNumber(metrics?.repeatDonors)}
                    </Typography>
                    <Loyalty fontSize="inherit" color="disabled" sx={{ mt: '2px' }} />
                  </Stack>
                </Box>
                
                <Button 
                  className="metric-action-button"
                  variant="outlined"
                  size="small" 
                  color="primary"
                  endIcon={<ArrowForward />}
                  onClick={onOpenDonorsModal}
                  sx={{ 
                    mt: 0, 
                    borderRadius: '12px',
                    opacity: { xs: 1, md: 0.8 },
                    transform: { xs: 'none', md: 'translateX(8px)' },
                    transition: 'opacity 0.3s, transform 0.3s',
                  }}
                >
                  View All
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Volunteers */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              borderRadius: 2,
              boxShadow: theme.shadows[3],
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
                '& .metric-action-button': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
                '& .metric-icon': {
                  transform: 'scale(1.1)',
                }
              }
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                height: '6px', 
                background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)` 
              }} 
            />
            
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    className="metric-icon"
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.15), 
                      color: theme.palette.success.main,
                      p: 1.5,
                      width: 56,
                      height: 56,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <Diversity3 fontSize="large" />
                  </Avatar>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: -0.5 }}>
                      TOTAL VOLUNTEERS
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        letterSpacing: '-0.5px',
                        color: theme.palette.success.dark
                      }}
                      aria-live="polite"
                    >
                      {formatNumber(metrics?.totalVolunteers)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Tooltip title={tooltipData.volunteers}>
                    <IconButton size="small" sx={{ mb: 0.5 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Chip 
                    icon={volunteerGrowth >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    label={`${Math.abs(volunteerGrowth).toFixed(1)}%`}
                    color={volunteerGrowth >= 0 ? "success" : "error"}
                    size="small"
                    variant="filled"
                    sx={{ 
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      boxShadow: theme.shadows[1]
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mt: 3, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress to goal
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                    {volunteersProgress.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(volunteersProgress, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 5,
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`
                    }
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 2, opacity: 0.6 }} />
              
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Active This Month
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatNumber(Math.round((metrics?.totalVolunteers || 0) * 0.7))}
                    </Typography>
                    <AssignmentInd fontSize="inherit" color="disabled" sx={{ mt: '2px' }} />
                  </Stack>
                </Box>
                
                <Button 
                  className="metric-action-button"
                  variant="outlined"
                  size="small" 
                  color="success"
                  endIcon={<ArrowForward />}
                  onClick={onOpenVolunteersModal}
                  sx={{ 
                    mt: 0, 
                    borderRadius: '12px',
                    opacity: { xs: 1, md: 0.8 },
                    transform: { xs: 'none', md: 'translateX(8px)' },
                    transition: 'opacity 0.3s, transform 0.3s',
                  }}
                >
                  View All
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Zoom>
  );
};

export default MetricsCards; 